import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'utils/hooks/useDebounce';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import {
  Container,
  FirstRow,
  InputWrapper,
  LeftSide,
  RightSide,
  Heading,
  NoContentLabelWrapper,
  NoContentLabel,
  CompaniesWrapper,
  Wrapper,
} from './SuppliersPage.styled';
import Input from 'components/Input/Input';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import { useGetCompanySuppliers } from './hooks';
import { AddBuyerModal } from 'components/Modals/AddBuyerModal/AddBuyerModal';
import { SUPPLIERS_PER_PAGE } from './constants';
import { RoutesConfig } from 'navigation/routes';
import { useNavigate } from 'react-router-dom';
import { ICompany } from 'types/Company.types';
import CompanyCard from 'components/CompanyCard/CompanyCard';
import Pagination from 'components/Pagination/Pagination';
import CompanyCardSkeleton from 'components/CompanyCard/CompanyCardSkeleton/CompanyCardSkeleton';
import { IDealerPanelSupplierDataFromNavigationState } from 'pages/DealerPanel/DealerPanelModelsPage/hooks';

const SuppliersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchBy, setSearchBy] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const debouncedSearchBy = useDebounce(searchBy);
  const [isAddBuyerModalOpen, setIsAddBuyerModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  usePageTitle('Suppliers');

  const handleOpenSupplier = (company: ICompany) => {
    const supplierData: IDealerPanelSupplierDataFromNavigationState = {
      supplierId: company.id,
      supplierHasSystemIntegration: company.has_system_integration,
    };
    navigate(RoutesConfig.DealerPanelModels.fullPath, {
      state: supplierData,
    });
  };

  const { data: suppliersData, isLoading: isLoadingSuppliers } =
    useGetCompanySuppliers(page, SUPPLIERS_PER_PAGE, debouncedSearchBy);

  return (
    <Container>
      <FirstRow>
        <LeftSide>
          <Heading>{t('Suppliers')}</Heading>
        </LeftSide>
        <RightSide>
          <InputWrapper>
            <Input
              placeholder={t('Search')}
              width={'100%'}
              onChange={(e: any) => setSearchBy(e.target.value)}
              icon={Search}
              wrapperStyles={{ width: '100%' }}
            />
          </InputWrapper>
        </RightSide>
      </FirstRow>
      {!suppliersData?.companies.length && !isLoadingSuppliers ? (
        <NoContentLabelWrapper>
          <NoContentLabel>{t('No suppliers available')}</NoContentLabel>
        </NoContentLabelWrapper>
      ) : (
        <Wrapper>
          <CompaniesWrapper>
            {suppliersData?.companies.map(
              (company: ICompany, index: number) => (
                <CompanyCard
                  key={`company-card-${index}`}
                  company={company}
                  onClick={(company: ICompany) => {
                    handleOpenSupplier(company);
                  }}
                  notSelectable={true}
                />
              )
            )}
            {isLoadingSuppliers
              ? Array.from({ length: SUPPLIERS_PER_PAGE }, (_, index) => (
                  <CompanyCardSkeleton
                    key={index}
                    index={index}
                    opacityCoefficient={0.1}
                  />
                ))
              : null}
          </CompaniesWrapper>
          {suppliersData?.pagination?.total > SUPPLIERS_PER_PAGE && (
            <Pagination
              page={page}
              perPage={SUPPLIERS_PER_PAGE}
              total={suppliersData?.pagination?.total}
              setPage={setPage}
            />
          )}
        </Wrapper>
      )}

      <AddBuyerModal
        isOpen={isAddBuyerModalOpen}
        setIsOpen={setIsAddBuyerModalOpen}
      />
    </Container>
  );
};

export default SuppliersPage;
